/**
 * Sub-hero Roll On Extrait — parallax por camadas.
 *
 * Contrato com o CSS:
 *   .sbr-frasco  posiciona pelo centro E recebe o parallax. É o único elemento
 *                cujo transform este arquivo reescreve — e por isso o
 *                translate(-50%,-50%) do centramento tem que ser reescrito
 *                junto, senão o frasco pula meia largura para a direita.
 *                A sombra vive aqui: conteúdo parado dentro do filtro deixa o
 *                navegador rasterizar o desfoque uma vez e só deslizar.
 *   .sbr-para    carrega só a rotação, escrita pelo Liquid. Não tocamos.
 *   .sbr-lift    é do hover, interno aos dois. Nenhum se sobrescreve.
 *
 * Custo por frame: um getBoundingClientRect na faixa + aritmética em <= 8 nós.
 * Tudo que depende de layout (altura, topo, meia-altura girada) é medido em
 * measure(), que só roda em resize e ao entrar em vista.
 */
(() => {
  if (customElements.get('subhero-rollon')) return;

  const AMPLITUDE = -74; // px, o mesmo do protótipo
  const LIMITE_P = 1.2;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  class SubheroRollon extends HTMLElement {
    connectedCallback() {
      this.quieto = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.quieto) return; // composição estática, sem listener nenhum

      this.faixa = this.querySelector('.sbr-faixa');
      if (!this.faixa) return;

      this.mobile = matchMedia('(max-width: 860px)');
      this.intensidade = parseFloat(this.dataset.intensidade);
      if (!Number.isFinite(this.intensidade)) this.intensidade = 1;

      this.itens = Array.from(this.querySelectorAll('.sbr-frasco')).map((frasco) => ({
        frasco,
        para: frasco.querySelector('.sbr-para'),
        vel: parseFloat(frasco.dataset.vel) || 0,
        rot: parseFloat(frasco.querySelector('.sbr-para')?.dataset.rot) || 0, // só para medir a caixa girada
        subidaMax: 0,
      })).filter((i) => i.para);

      if (!this.itens.length) return;

      this.visivel = false;
      this.pendente = false;

      this.aoScroll = () => {
        if (!this.visivel || this.pendente) return;
        this.pendente = true;
        requestAnimationFrame(this.frame);
      };
      this.frame = this.frame.bind(this);
      this.aoResize = () => {
        this.measure();
        this.pendente = true;
        requestAnimationFrame(this.frame);
      };

      this.io = new IntersectionObserver((entradas) => {
        this.visivel = entradas.some((e) => e.isIntersecting);
        if (this.visivel) {
          this.measure();
          this.pendente = true;
          requestAnimationFrame(this.frame);
        }
      }, { rootMargin: '120px 0px' });
      this.io.observe(this);

      addEventListener('scroll', this.aoScroll, { passive: true });
      addEventListener('resize', this.aoResize, { passive: true });

      this.measure();
    }

    disconnectedCallback() {
      if (this.quieto) return;
      this.io?.disconnect();
      removeEventListener('scroll', this.aoScroll);
      removeEventListener('resize', this.aoResize);
    }

    /**
     * Quanto cada frasco pode subir sem cruzar a borda de cima da faixa.
     * A faixa fica colada no hero: um frasco saindo por cima pisaria nele.
     * Por isso o teto é uma invariante do código, não uma promessa do setting.
     */
    measure() {
      for (const item of this.itens) {
        const { frasco } = item;
        if (!frasco.offsetParent) { item.subidaMax = 0; continue; } // escondido no mobile

        const h = frasco.offsetHeight;
        const w = frasco.offsetWidth;
        const rad = (item.rot * Math.PI) / 180;
        // Meia-altura da caixa DEPOIS de girar — girar um frasco alto engorda a caixa.
        const meia = (Math.abs(h * Math.cos(rad)) + Math.abs(w * Math.sin(rad))) / 2;
        const topo = frasco.offsetTop - meia;
        const pe = frasco.offsetTop + meia;
        const palco = frasco.offsetParent ? frasco.offsetParent.offsetHeight : 0;

        // Um frasco que sangra por baixo não pode subir a ponto de abrir vão
        // entre o pé dele e a borda. Quem não sangra não tem esse limite.
        const ateOPe = pe > palco ? pe - palco : Infinity;
        item.subidaMax = Math.max(0, Math.min(topo, ateOPe));
      }
    }

    frame() {
      this.pendente = false;
      if (!this.visivel) return;

      const r = this.faixa.getBoundingClientRect();
      const vh = innerHeight;
      const p = clamp(((r.top + r.height / 2) - vh / 2) / vh, -LIMITE_P, LIMITE_P);

      const intensidade = this.mobile.matches ? this.intensidade / 2 : this.intensidade;

      for (const item of this.itens) {
        let dy = p * item.vel * intensidade * AMPLITUDE;
        if (dy < 0) dy = Math.max(dy, -item.subidaMax); // trava na borda de cima
        item.frasco.style.transform =
          `translate(-50%, -50%) translate3d(0, ${dy.toFixed(1)}px, 0)`;
      }
    }
  }

  customElements.define('subhero-rollon', SubheroRollon);
})();
